import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { getCookie } from "../utils/cookieUtils";
import { useAchievement } from './AchievementContext';
import * as Sentry from "@sentry/react"
import { getPartitionedLocalStorage } from "../utils/tokenExtraction";
const GameContext = createContext();

export const GameProvider = ({ children }) => {
  const [gamificationData, setGamificationData] = useState({
    full_name: "",
    tasks: [],
    streak: 0,
    completedDays: [],
    ai_response_count: 0,
    rank: 0,
    triggeredMilestones: []
  });

  const [milestoneLock, setMilestoneLock] = useState(new Set());

  // Get showAchievement from Achievement context
  const { showAchievement } = useAchievement();

  const fetchGamificationData = async () => {
    try {
      const token = getCookie("token") || getPartitionedLocalStorage("token");
      const response = await axios.get(`${process.env.REACT_APP_SERVER_ENDPOINT}/api/fetchGamificationData`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response.data;
      console.log(data)

      if (data.success && data.tasks?.[0]) {
        const { user_info, taskdata } = data.tasks[0];

        setGamificationData({
          full_name: user_info?.full_name || "Guest",
          tasks: taskdata || [],
          streak: user_info?.streak || 0,
          completedDays: user_info?.completed_days || [],
          ai_response_count: user_info?.ai_response_count || 0,
          rank: user_info?.rank || 0,
          triggeredMilestones: user_info?.milestones || []
        });
      } else {
        console.warn("No gamification data available.");
      }
    } catch (err) {
      console.error("Error fetching gamification data:", err);
      Sentry.captureException(err);
    }
  };

  const completeTask = async (taskKey) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/complete-task`,
        { taskKey },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        const completedAt = response.data.completedAt;

        setGamificationData((prev) => ({
          ...prev,
          tasks: prev.tasks.map((task) =>
            task.taskKey === taskKey
              ? { ...task, is_completed: true, completed_at: completedAt }
              : task
          ),
        }));
      }
    } catch (error) {
      console.error("Error completing task:", error);
      Sentry.captureException(error);
    }
  };

  const checkAndTriggerMilestone = async (currentProgress) => {
    const milestoneThresholds = [
      { 
        threshold: 33, 
        taskKey: 'first_milestone', 
        label: '5 Minutes Milestone',
        icon: '/UpdateMinutes.svg' 
      },
      { 
        threshold: 66, 
        taskKey: 'second_milestone', 
        label: '10 Minutes Milestone',
        icon: '/UpdateMinutes.svg'
      },
      { 
        threshold: 98, 
        taskKey: 'third_milestone', 
        label: '15 Minutes Milestone',
        icon: '/UpdateMinutes.svg'
      }
    ];
    console.log("Milestone thresholds:", milestoneThresholds);

    for (const milestone of milestoneThresholds) {
      const isMilestoneTriggered = gamificationData?.triggeredMilestones?.includes(milestone.taskKey);

      console.log("Data : ")
      console.log("Gamfication Data :", gamificationData);
      console.log("isMilestoneTriggered :", isMilestoneTriggered);
      console.log("Milestone Lock :", milestoneLock.has(milestone.taskKey));

      if (
        currentProgress >= milestone.threshold &&
        !isMilestoneTriggered &&
        !milestoneLock.has(milestone.taskKey)
      ) {
        try {
          console.log(`Triggering milestone: ${milestone.taskKey}`);
          const token = getCookie("token")  || getPartitionedLocalStorage("token");

          setMilestoneLock(prev => new Set([...prev, milestone.taskKey]));

          const response = await axios.post(
            `${process.env.REACT_APP_SERVER_ENDPOINT}/api/milestoneReward`,
            {
              taskKey: milestone.taskKey,
              userId: gamificationData.userId,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (response.data.success === true) {
            // Show achievement modal before fetching new data
            // showAchievement(milestone.label, {
            //   ...milestone,
            //   description: `You've unlocked ${milestone.label}!`
            // });
            
            await fetchGamificationData();
          }
        } catch (error) {
          console.error(`Error triggering milestone ${milestone.threshold}:`, error);
          Sentry.captureException(error);
          setMilestoneLock(prev => {
            const newSet = new Set(prev);
            newSet.delete(milestone.taskKey);
            return newSet;
          });
        }
      }
    }
  };

  useEffect(() => {
    const completedTasksCount = gamificationData?.tasks?.filter(task => task.is_completed).length || 0;
    const totalTasks = gamificationData?.tasks?.length || 0;
    const progress = totalTasks > 0 ? Math.floor((completedTasksCount / totalTasks) * 100) : 0;

    if (progress > 0 && gamificationData?.tasks) {
      checkAndTriggerMilestone(progress);
    }
  }, [gamificationData?.tasks]);

  return (
    <GameContext.Provider 
      value={{ 
        gamificationData, 
        completeTask, 
        setGamificationData,
        fetchGamificationData,
        checkAndTriggerMilestone 
      }}
    >
      {children}
    </GameContext.Provider>
  );
};

export const useGameContext = () => useContext(GameContext);